import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  deleteCrossClientLandingExecution,
  selectCrossClientLandingDecompositionResult,
  setCrossClientLandingDecompositionResult,
  setCrossClientEvaluateLoading,
  setCrossClientInitLoading,
} from '../../../store/crossClient/crossClientLanding'
import { useDecomposeCrossClientQueryMutation, useEvaluateClauseResolversCrossClientQueryMutation } from '../../../store/crossClient/crossClientExecution'
import { useDeleteCrossClientQueryExecutionMutation } from '../../../store/crossClient/crossClient'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  type CrossClientQueryAtomicClause,
  type CrossClientQueryClauseResolverMapping,
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
  type CrossClientQueryResultSummary,
} from '../../../models/CrossClient'

interface DecomposeArgs {
  userQuestion: string
  sources: CrossClientQueryDataSource[]
  clientQueryMode: CrossClientQueryClientQueryMode
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
  clauseResolverMapping?: CrossClientQueryClauseResolverMapping
}

interface DecomposeOptions {
  skipReviewPipeline?: boolean
}

interface ReviewCommonFields {
  atomicClauses: CrossClientQueryAtomicClause[]
  hasLocal: boolean
  sources: CrossClientQueryDataSource[]
}

export type CrossClientReviewState =
  | ({ kind: 'bulk-running' } & ReviewCommonFields)
  | ({ kind: 'bulk-done'; resultSummary: CrossClientQueryResultSummary } & ReviewCommonFields)
  | ({ kind: 'no-bulk' } & ReviewCommonFields)

export const useCrossClientQueryLandingExecution = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const decompositionResult = useAppSelector(selectCrossClientLandingDecompositionResult)

  const [triggerDecomposeCrossClientQuery] = useDecomposeCrossClientQueryMutation()
  const [triggerEvaluateClauseResolvers] = useEvaluateClauseResolversCrossClientQueryMutation()
  const [triggerDeleteCrossClientExecution] = useDeleteCrossClientQueryExecutionMutation()

  const [reviewState, setReviewState] = useState<CrossClientReviewState | null>(null)

  const openResults = useCallback(
    (executionId: string) => {
      navigate(`/cross-client/executions/${executionId}`, { replace: true })
    },
    [navigate],
  )

  const decomposeCrossClientQuery = useCallback(
    async (args: DecomposeArgs, options?: DecomposeOptions) => {
      let result
      try {
        dispatch(setCrossClientLandingDecompositionResult(null))
        setReviewState(null)
        dispatch(setCrossClientInitLoading(true))
        result = await triggerDecomposeCrossClientQuery({
          accountId,
          payload: args,
        }).unwrap()
        dispatch(setCrossClientLandingDecompositionResult(result))
      } catch (e) {
        console.error(e)
        return
      } finally {
        dispatch(setCrossClientInitLoading(false))
      }

      if (options?.skipReviewPipeline) {
        return result
      }

      const atomicClauses = result.decomposition?.atomicClauses ?? []
      if (atomicClauses.length === 0) {
        return result
      }

      const hasBulk = result.resolvableClauseVariables.length > 0
      const hasLocal = result.localResolvableClauseVariables.length > 0
      const common: ReviewCommonFields = { atomicClauses, hasLocal, sources: args.sources }

      if (!hasBulk) {
        setReviewState({ kind: 'no-bulk', ...common })
        return result
      }

      setReviewState({ kind: 'bulk-running', ...common })
      try {
        const resultSummary = await triggerEvaluateClauseResolvers({ accountId, executionId: result.executionId }).unwrap()
        setReviewState({ kind: 'bulk-done', resultSummary, ...common })
      } catch (e) {
        console.error(e)
        setReviewState({ kind: 'no-bulk', ...common })
      }
      return result
    },
    [dispatch, accountId, triggerDecomposeCrossClientQuery, triggerEvaluateClauseResolvers],
  )

  const initError = useMemo(() => {
    if (!decompositionResult) {
      return null
    }
    if (!decompositionResult.decomposition?.atomicClauses?.length) {
      return 'Unable to decompose question'
    }
    return null
  }, [decompositionResult])

  const resetExecution = useCallback(() => {
    dispatch(setCrossClientLandingDecompositionResult(null))
    setReviewState(null)
  }, [dispatch])

  const discardExecution = useCallback(async () => {
    const executionId = decompositionResult?.executionId
    if (executionId) {
      try {
        await triggerDeleteCrossClientExecution({ accountId, executionId }).unwrap()
        dispatch(deleteCrossClientLandingExecution(executionId))
      } catch (e) {
        console.error(e)
      }
    }
    resetExecution()
  }, [accountId, decompositionResult?.executionId, triggerDeleteCrossClientExecution, dispatch, resetExecution])

  const finalizeExecution = useCallback(
    async (executionId: string) => {
      dispatch(setCrossClientEvaluateLoading(true))
      openResults(executionId)
    },
    [dispatch, openResults],
  )

  return {
    initError,
    reviewState,
    decomposeCrossClientQuery,
    finalizeExecution,
    resetExecution,
    discardExecution,
  }
}
